/**
 * Shared-leaderboard display name (issue #35). The nickname lives in `UserSettings`
 * (so it syncs across devices via the Firestore mirror), but the default-generation and
 * Google-seed policy live here to keep `userSettings` free of identity concerns.
 *
 * Default identity is low-friction: a player who never picks a name still shows up as
 * `Spadaccino-####`, generated once and then persisted (stable across runs).
 */
import { LEADERBOARD_NICKNAME_MAX_LEN } from '../config/constants';
import { getSetting, setSetting } from './userSettings';

const DEFAULT_PREFIX = 'Spadaccino';

/** A fresh `Spadaccino-####` (four digits, 1000–9999). */
export function generateDefaultNickname(): string {
  const n = Math.floor(1000 + Math.random() * 9000);
  return `${DEFAULT_PREFIX}-${n}`;
}

/**
 * The nickname to display/submit. If the player has never set one, generate a default,
 * persist it (so it stays stable across runs and syncs to the cloud), and return it.
 */
export function getEffectiveNickname(): string {
  const current = getSetting('nickname').trim();
  if (current) return current;
  const generated = generateDefaultNickname();
  setSetting('nickname', generated);
  return generated;
}

/** Persist a player-chosen nickname (trimmed + clamped to the leaderboard max length). */
export function setNickname(name: string): void {
  setSetting('nickname', name.trim().slice(0, LEADERBOARD_NICKNAME_MAX_LEN));
}

/**
 * Seed the nickname from a Google `displayName`, but only when the player hasn't set one
 * yet — never overwrite a chosen name on sign-in/link.
 */
export function seedNicknameFromDisplayName(displayName: string | null | undefined): void {
  if (getSetting('nickname').trim()) return;
  const clean = (displayName ?? '').trim().slice(0, LEADERBOARD_NICKNAME_MAX_LEN);
  if (clean) setSetting('nickname', clean);
}
